import { useState, useCallback } from 'react';
import { pipelinesAPI } from '../services/api';
import { useToast } from './use-toast';

export const usePipelineManagement = () => {
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [createPipelineDialog, setCreatePipelineDialog] = useState(false);
  const [editPipelineDialog, setEditPipelineDialog] = useState(false);
  const [editPipeline, setEditPipeline] = useState(null);
  const [deletePipelineDialog, setDeletePipelineDialog] = useState(false);
  const [pipelineToDelete, setPipelineToDelete] = useState(null);
  const [newPipeline, setNewPipeline] = useState({
    name: '',
    code: '',
    stages: [
      { id: 's1', name: 'Stage 1', color: '#3B82F6', tasks: [] },
      { id: 's2', name: 'Stage 2', color: '#10B981', tasks: [] },
      { id: 's3', name: 'Stage 3', color: '#F59E0B', tasks: [] }
    ]
  });
  const { toast } = useToast();

  const fetchPipelines = useCallback(async () => {
    try {
      const response = await pipelinesAPI.getAll();
      const pipelineList = response.data.pipelines || [];
      setPipelines(pipelineList);
      if (pipelineList.length > 0 && !selectedPipeline) {
        setSelectedPipeline(pipelineList[0]);
      }
      return pipelineList;
    } catch (error) {
      console.error('Error fetching pipelines:', error);
      return [];
    }
  }, [selectedPipeline]);

  const handleCreatePipeline = useCallback(async () => {
    if (!newPipeline.name) {
      toast({ title: 'Error', description: 'Pipeline name is required', variant: 'destructive' });
      return;
    }

    try {
      const pipelineData = {
        ...newPipeline,
        code: newPipeline.code || newPipeline.name.toUpperCase().replace(/\s+/g, '_').substring(0, 10),
        stages: newPipeline.stages.map((stage, idx) => ({
          ...stage,
          id: stage.id || `stage_${idx + 1}`
        }))
      };

      await pipelinesAPI.create(pipelineData);
      toast({ title: 'Success', description: 'Pipeline created successfully' });
      setCreatePipelineDialog(false);
      setNewPipeline({
        name: '',
        code: '',
        stages: [
          { id: 's1', name: 'Stage 1', color: '#3B82F6', tasks: [] },
          { id: 's2', name: 'Stage 2', color: '#10B981', tasks: [] },
          { id: 's3', name: 'Stage 3', color: '#F59E0B', tasks: [] }
        ]
      });
      fetchPipelines();
    } catch (error) {
      console.error('Error creating pipeline:', error);
      toast({ title: 'Error', description: 'Failed to create pipeline', variant: 'destructive' });
    }
  }, [newPipeline, toast, fetchPipelines]);

  const openEditPipelineDialog = useCallback((pipeline) => {
    setEditPipeline({ ...pipeline });
    setEditPipelineDialog(true);
  }, []);

  const handleUpdatePipeline = useCallback(async () => {
    if (!editPipeline?.name) {
      toast({ title: 'Error', description: 'Pipeline name is required', variant: 'destructive' });
      return;
    }

    try {
      await pipelinesAPI.update(editPipeline._id, {
        name: editPipeline.name,
        code: editPipeline.code,
        stages: editPipeline.stages
      });
      toast({ title: 'Success', description: 'Pipeline updated' });
      setEditPipelineDialog(false);
      setEditPipeline(null);
      const updatedPipelines = await fetchPipelines();
      const updated = updatedPipelines.find(p => p._id === editPipeline._id);
      if (updated) setSelectedPipeline(updated);
    } catch (error) {
      console.error('Error updating pipeline:', error);
      toast({ title: 'Error', description: 'Failed to update pipeline', variant: 'destructive' });
    }
  }, [editPipeline, toast, fetchPipelines]);

  const openDeletePipelineDialog = useCallback((pipeline) => {
    setPipelineToDelete(pipeline);
    setDeletePipelineDialog(true);
  }, []);

  const handleDeletePipeline = useCallback(async () => {
    if (!pipelineToDelete) return;

    try {
      await pipelinesAPI.delete(pipelineToDelete._id);
      toast({ title: 'Success', description: `Pipeline "${pipelineToDelete.name}" deleted` });
      setDeletePipelineDialog(false);
      setPipelineToDelete(null);
      if (selectedPipeline?._id === pipelineToDelete._id) {
        setSelectedPipeline(null);
      }
      fetchPipelines();
    } catch (error) {
      console.error('Error deleting pipeline:', error);
      toast({ title: 'Error', description: 'Failed to delete pipeline', variant: 'destructive' });
    }
  }, [pipelineToDelete, selectedPipeline, toast, fetchPipelines]);

  // Stage management within pipeline
  const addStage = useCallback((setPipelineState) => {
    setPipelineState(prev => ({
      ...prev,
      stages: [
        ...prev.stages,
        { id: `s${Date.now()}`, name: `Stage ${prev.stages.length + 1}`, color: '#6B7280', tasks: [] }
      ]
    }));
  }, []);

  const removeStage = useCallback((index, setPipelineState) => {
    setPipelineState(prev => ({
      ...prev,
      stages: prev.stages.filter((_, i) => i !== index)
    }));
  }, []);

  const updateStage = useCallback((index, field, value, setPipelineState) => {
    setPipelineState(prev => ({
      ...prev,
      stages: prev.stages.map((stage, i) =>
        i === index ? { ...stage, [field]: value } : stage
      )
    }));
  }, []);

  const addTaskToStage = useCallback((stageIndex, setPipelineState) => {
    setPipelineState(prev => ({
      ...prev,
      stages: prev.stages.map((stage, i) =>
        i === stageIndex
          ? { ...stage, tasks: [...(stage.tasks || []), { id: `t${Date.now()}`, name: '', description: '' }] }
          : stage
      )
    }));
  }, []);

  const removeTaskFromStage = useCallback((stageIndex, taskIndex, setPipelineState) => {
    setPipelineState(prev => ({
      ...prev,
      stages: prev.stages.map((stage, i) =>
        i === stageIndex
          ? { ...stage, tasks: stage.tasks.filter((_, ti) => ti !== taskIndex) }
          : stage
      )
    }));
  }, []);

  const updateTask = useCallback((stageIndex, taskIndex, field, value, setPipelineState) => {
    setPipelineState(prev => ({
      ...prev,
      stages: prev.stages.map((stage, i) =>
        i === stageIndex
          ? {
              ...stage,
              tasks: stage.tasks.map((task, ti) =>
                ti === taskIndex ? { ...task, [field]: value } : task
              )
            }
          : stage
      )
    }));
  }, []);

  return {
    pipelines,
    setPipelines,
    selectedPipeline,
    setSelectedPipeline,
    createPipelineDialog,
    setCreatePipelineDialog,
    editPipelineDialog,
    setEditPipelineDialog,
    editPipeline,
    setEditPipeline,
    deletePipelineDialog,
    setDeletePipelineDialog,
    pipelineToDelete,
    newPipeline,
    setNewPipeline,
    fetchPipelines,
    handleCreatePipeline,
    openEditPipelineDialog,
    handleUpdatePipeline,
    openDeletePipelineDialog,
    handleDeletePipeline,
    addStage,
    removeStage,
    updateStage,
    addTaskToStage,
    removeTaskFromStage,
    updateTask
  };
};
